import {
  Button,
  FlexBox,
  FormField,
  FormGrid,
  Page,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { AsyncStatus } from 'lib-shared-types-api';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { webformEndpointsApi } from 'apis/webformEndpoints';
import { HookFormEmailSelect, HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { articles } from 'utils/elevio';
import { validateName } from 'utils/formValidator';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { SettingsIntegrationsDocumentationButton } from '../../../../components/documentationButton/documentationButton';
import { pathToWebformEndpointsSettings } from '../../webformEndpoints.view';

type WebformEndpointsCreateFormValues = {
  emailIntegrationId: string[];
  description: string;
  redirectUrl?: string;
};

/** Settings page to create a new webform endpoint. */
export const WebformEndpointsCreateView = (): JSX.Element => {
  const navigate = useAppNavigate();
  const notify = useNotifications();

  const [getEmailAsyncStatus, setGetEmailAsyncStatus] = useState<AsyncStatus>();

  const [createWebformEndpoint, { isLoading: isCreating }] =
    webformEndpointsApi.useCreateWebformEndpointMutation();

  const { control, handleSubmit } = useForm<WebformEndpointsCreateFormValues>({
    defaultValues: {
      emailIntegrationId: [],
      description: '',
    },
    mode: 'onChange',
  });

  const onSubmit: SubmitHandler<WebformEndpointsCreateFormValues> = async ({
    emailIntegrationId,
    description,
    redirectUrl,
  }) => {
    const response = await createWebformEndpoint({
      description,
      email_integration_id: emailIntegrationId[0],
      redirect_uri: redirectUrl,
    }).unwrap();

    if (isResponseWithData(response)) {
      notify(
        {
          title: 'Success',
          text: `"${response.data?.description}" has been created.`,
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );

      ampli.settingsIntegrationsWebformEndpointsAddSave({
        hasRedirectUrl: !!redirectUrl,
      });

      navigate({ path: pathToWebformEndpointsSettings });
    } else {
      notify(
        {
          title: 'Error while creating the webform endpoint.',
        },
        NotificationType.Error,
      );
    }
  };

  if (getEmailAsyncStatus === 'rejected') {
    return (
      <FlexBox justifyContent="center" alignItems="center">
        There was a problem getting the emails.
      </FlexBox>
    );
  }

  return (
    <Page.Section
      title="Create webform endpoint"
      titleMeta={
        <SettingsIntegrationsDocumentationButton
          onClick={() => {
            ampli.settingsIntegrationsWebformEndpointsReadDocumentationLink();

            elevioClient.openArticle(
              articles.settings.webformEndpoints.documentation,
            );
          }}
        />
      }
      controls={
        <Button
          isLoading={isCreating}
          disabled={isCreating || getEmailAsyncStatus === 'pending'}
          onClick={handleSubmit(onSubmit)}
        >
          Save changes
        </Button>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormGrid>
          <FormField span={3}>
            <HookFormEmailSelect
              control={control}
              name="emailIntegrationId"
              label="Email"
              componentProps={{
                onEmailAsyncStatusChange: setGetEmailAsyncStatus,
              }}
              helpIcon={{
                dark: true,
                title: 'Email address',
                text: 'Choose the email address that will be used to route the conversations that you are creating from your webform.',
              }}
            />
          </FormField>
          <FormField span={3} />
          <FormField span={3}>
            <HookFormText
              control={control}
              label="Name"
              name={'description'}
              rules={{
                required: {
                  value: true,
                  message: 'Please add a name',
                },
                validate: validateName,
              }}
              helpIcon={{
                dark: true,
                title: 'Endpoint name',
                text: 'Type a name here, so you can recognize your webform integration easily later.',
              }}
              componentProps={{
                placeholder: 'Insert name',
              }}
            />
          </FormField>
          <FormField span={3} />
          <FormField span={3}>
            <HookFormText
              control={control}
              label="Redirect URL"
              name={'redirectUrl'}
              helpIcon={{
                dark: true,
                title: 'Redirect URL',
                text: 'In case you are using the webform integration directly in your website code, please choose a redirect URL where the user should be directed to after submitting the webform.',
              }}
              componentProps={{
                placeholder: 'Insert redirect URL',
              }}
            />
          </FormField>
          <FormField span={3} />
        </FormGrid>
      </form>
    </Page.Section>
  );
};
